import { zodResolver } from "@hookform/resolvers/zod";
import { PlusIcon, TrashIcon } from "lucide-react";
import { useState } from "react";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import type { z } from "zod";

import { createAssociation } from "@blis/shared/schemas/workflow";
import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { Button } from "@/components/ui/button";
import {
  ComboBox,
  ComboBoxInput,
  ComboBoxItem,
  ComboBoxList,
} from "@/components/ui/combobox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FieldError, Label } from "@/components/ui/field";
import { ItemDetail } from "@/components/ui/list-box";
import { Modal } from "@/components/ui/modal";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import type { WorkflowDetail } from "../../types";
import { useAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TabLayout } from "../TabLayout";
import { TableFilterRow } from "../TableFilterRow";

type FormValues = z.infer<typeof createAssociation>;

export function WorkflowAssociationsTable({
  workflow,
}: {
  workflow: WorkflowDetail;
}) {
  const { options, filterTextProps } = useAdminGridOptions(
    workflow.associations,
    [
      {
        headerName: "Name",
        flex: 1,
        minWidth: 200,
        valueGetter: (params) => {
          if (!params.data) {
            return "";
          }
          return (
            params.data.testResultSpec?.name ?? params.data.testSpec?.name ?? ""
          );
        },
        cellRenderer: (params) => {
          if (params.data.testResultSpec) {
            return <div>{params.data.testResultSpec.name}</div>;
          }
          return <div>{params.data.testSpec.name}</div>;
        },
      },
      {
        headerName: "Code",
        width: 140,
        maxWidth: 200,
        valueGetter: (params) => {
          if (params.data?.testResultSpec) return "";
          return params.data?.testSpec?.code ?? "";
        },
        cellRenderer: (params) => {
          if (params.data?.testResultSpec) return null;
          const code = params.data?.testSpec?.code;
          if (!code) {
            return (
              <Text muted size="2">
                —
              </Text>
            );
          }
          return (
            <Text className="font-mono tabular-nums" size="2">
              {code}
            </Text>
          );
        },
      },
      {
        headerName: "Type",
        cellRenderer: (params) => {
          return (
            <div>
              {params.data.testResultSpec ? "Test Result" : "Test Spec"}
            </div>
          );
        },
        cellRendererParams: (params) => ({
          testResultSpec: params.data.testResultSpec,
          testSpec: params.data.testSpec,
        }),
      },
      {
        headerName: "Actions",
        cellRenderer: (params) => {
          return <RemoveAssociationDialog association={params.data} />;
        },
      },
    ],
    { sideBar: undefined },
  );

  return (
    <TabLayout>
      <TableFilterRow inputProps={filterTextProps}>
        <AddAssociationDialog workflow={workflow} />
      </TableFilterRow>
      <div className="flex h-96">
        <AdminGrid {...options} />
      </div>
    </TabLayout>
  );
}

function AddAssociationDialog({ workflow }: { workflow: WorkflowDetail }) {
  const methods = useForm<FormValues>({
    defaultValues: {
      workflowId: workflow.id,
    },
    resolver: zodResolver(createAssociation),
  });
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<"testResultSpec" | "testSpec" | undefined>(
    "testSpec",
  );

  const _typeOptions = [
    { label: "Test spec", value: "testSpec" },
    { label: "Test result spec", value: "testResultSpec" },
  ];

  const mutation = trpc.admin.workflow.createAssociation.useMutation();

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    mutation.mutate(
      {
        workflowId: workflow.id,
        testResultSpecId: values.testResultSpecId,
        testSpecId: values.testSpecId,
      },
      mutationResponse({
        errorTitle: "Error adding association",
        successMessage: "Association added",
        onSuccess() {
          setOpen(false);
          methods.reset();
        },
      }),
    );
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button variant="secondary" type="button">
        <PlusIcon data-slot="icon" />
        Add association
      </Button>
      <Modal isOpen={open} onOpenChange={setOpen}>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Add association</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              id="add-association-form"
              onSubmit={methods.handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              <FormProvider {...methods}>
                <Select
                  selectedKey={type}
                  onSelectionChange={(value) => setType(value as typeof type)}
                >
                  <Label>Type</Label>
                  <SelectTrigger />
                  <SelectList>
                    <SelectItem id="testSpec">Test spec</SelectItem>
                    <SelectItem id="testResultSpec">
                      Test result spec
                    </SelectItem>
                  </SelectList>
                </Select>
                {type === "testSpec" && <TestSpecSelect />}
                {type === "testResultSpec" && <TestResultSpecSelect />}
              </FormProvider>
            </form>
          </DialogBody>

          <DialogFooter>
            <Button
              type="submit"
              isPending={mutation.isPending}
              form="add-association-form"
            >
              Submit
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}

function TestResultSpecSelect() {
  const { control } = useFormContext<FormValues>();
  const { data: testResultSpecs } = trpc.admin.testResultSpec.list.useQuery();

  return (
    <Controller
      control={control}
      name="testResultSpecId"
      render={({ field, fieldState: { error } }) => (
        <ComboBox
          selectedKey={field.value}
          onSelectionChange={(value) => field.onChange(value)}
          isInvalid={!!error}
        >
          <Label>Test result spec</Label>
          <ComboBoxInput />
          <ComboBoxList items={testResultSpecs || []}>
            {(item) => (
              <ComboBoxItem
                id={item.id}
                textValue={`${item.name} (${item.testSpec.code})`}
              >
                <span className="min-w-0 flex-1 truncate">{item.name}</span>
                <ItemDetail>{item.testSpec.code}</ItemDetail>
              </ComboBoxItem>
            )}
          </ComboBoxList>
          <FieldError>{error?.message}</FieldError>
        </ComboBox>
      )}
    />
  );
}

function TestSpecSelect() {
  const { control } = useFormContext<FormValues>();
  const { data: testSpecs } = trpc.admin.testSpec.list.useQuery();

  return (
    <Controller
      control={control}
      name="testSpecId"
      render={({ field, fieldState: { error } }) => (
        <ComboBox
          selectedKey={field.value}
          onSelectionChange={(value) => field.onChange(value)}
          isInvalid={!!error}
        >
          <Label>Test spec</Label>
          <ComboBoxInput />
          <ComboBoxList items={testSpecs || []}>
            {(item) => (
              <ComboBoxItem
                id={item.id}
                textValue={`${item.name} (${item.code})`}
              >
                <span className="min-w-0 flex-1 truncate">{item.name}</span>
                <ItemDetail>{item.code}</ItemDetail>
              </ComboBoxItem>
            )}
          </ComboBoxList>
          <FieldError>{error?.message}</FieldError>
        </ComboBox>
      )}
    />
  );
}

function RemoveAssociationDialog({
  association,
}: {
  association: { id: number };
}) {
  const [open, setOpen] = useState(false);
  const deleteSpec = trpc.admin.workflow.deleteAssociation.useMutation();

  const onConfirm = () => {
    deleteSpec.mutate(
      {
        id: association.id,
      },
      mutationResponse({
        errorTitle: "Error removing association",
        successMessage: "Association removed",
        onSuccess() {
          setOpen(false);
        },
      }),
    );
  };
  return (
    <ConfirmPrompt
      open={open}
      onOpenChange={setOpen}
      danger
      title="Remove association"
      message="Are you sure you want to remove this association?"
      onConfirm={onConfirm}
      loading={deleteSpec.isPending}
    >
      <Button variant="destructive" type="button" size="icon-sm">
        <TrashIcon />
      </Button>
    </ConfirmPrompt>
  );
}
